import { useState } from 'react';
import { Input } from 'reactstrap';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import i18n from '@/utils/i18n';
import { EditItem } from '../../components/Layout';
import { Communicator } from '../../tools/Communication';
import { LayoutStoreInstance } from '../../LayoutStore';

type WebhookConfigSectionProps = {
  storeId: string;
  service: string;
  // Shown as the section title instead of the generic "Setup Webhooks" (used to
  // distinguish sections when several custom integrations are listed together).
  title?: string;
  reminderWebhookUrl: string | null;
  returnWebhookUrl: string | null;
  invoiceWebhookUrl: string | null;
  // Whether a webhook API key is already stored. The value is never sent to the frontend;
  // the field renders masked and, when left blank on save, keeps the stored key untouched.
  apiKeyIsSet: boolean;
  onSaved: () => Promise<void> | void;
};

// Editable webhook config for a custom integration. The URLs come from GetIntegrations
// (stored in the integration's own config entry); saving writes them back via
// UpdateWebhookConfig. Only rendered for custom integrations the store owns.
export const WebhookConfigSection = ({
  storeId,
  service,
  title,
  reminderWebhookUrl,
  returnWebhookUrl,
  invoiceWebhookUrl,
  apiKeyIsSet,
  onSaved,
}: WebhookConfigSectionProps) => {
  const [reminderEnabled, setReminderEnabled] = useState(!!reminderWebhookUrl);
  const [returnEnabled, setReturnEnabled] = useState(!!returnWebhookUrl);
  const [reminderUrl, setReminderUrl] = useState(reminderWebhookUrl ?? '');
  const [returnUrl, setReturnUrl] = useState(returnWebhookUrl ?? '');
  const [invoiceUrl, setInvoiceUrl] = useState(invoiceWebhookUrl ?? '');
  // Always starts empty (the stored secret is never sent to the SPA). A blank value on save
  // keeps the existing key; typing a value replaces it.
  const [apiKey, setApiKey] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const save = async () => {
    if (invoiceUrl.trim() === '') {
      LayoutStoreInstance.setError(
        i18n.t('Integrations:webhookInvoiceRequired'),
      );
      return;
    }
    setIsSaving(true);
    try {
      await Communicator.PostData('/Integration/UpdateWebhookConfig', {
        storeId,
        service,
        reminderWebhookUrl: reminderEnabled ? reminderUrl.trim() : '',
        returnWebhookUrl: returnEnabled ? returnUrl.trim() : '',
        invoiceWebhookUrl: invoiceUrl.trim(),
        // Omit when blank so the stored key is kept; only send a value to replace it.
        apiKey: apiKey.trim() === '' ? null : apiKey.trim(),
      });
      LayoutStoreInstance.setSuccess(i18n.t('Integrations:webhookSaved'));
      setApiKey('');
      await onSaved();
    } catch (e) {
      try {
        const body = await (e as Response).json();
        LayoutStoreInstance.setError(
          body.errorMessage ?? i18n.t('Integrations:webhookSaveError'),
        );
      } catch {
        LayoutStoreInstance.setError(i18n.t('Integrations:webhookSaveError'));
      }
    }
    setIsSaving(false);
  };

  return (
    <EditItem title={title ?? i18n.t('Integrations:setupWebhooks')}>
      <div className="mb-3">
        <label className="form-label fw-bold">
          {i18n.t('Integrations:webhookInvoice')}
        </label>
        <p className="text-muted small">
          {i18n.t('Integrations:webhookInvoiceHelp')}
        </p>
        <Input
          type="url"
          value={invoiceUrl}
          placeholder="https://..."
          onChange={(e) => setInvoiceUrl(e.target.value)}
        />
      </div>

      <div className="mb-3">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`reminder-toggle-${service}`}
            checked={reminderEnabled}
            onChange={(e) => setReminderEnabled(e.target.checked)}
          />
          <label
            className="form-check-label fw-bold"
            htmlFor={`reminder-toggle-${service}`}
          >
            {i18n.t('Integrations:webhookReminder')}
          </label>
        </div>
        <Input
          type="url"
          value={reminderUrl}
          placeholder="https://..."
          disabled={!reminderEnabled}
          onChange={(e) => setReminderUrl(e.target.value)}
        />
      </div>

      <div className="mb-3">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`return-toggle-${service}`}
            checked={returnEnabled}
            onChange={(e) => setReturnEnabled(e.target.checked)}
          />
          <label
            className="form-check-label fw-bold"
            htmlFor={`return-toggle-${service}`}
          >
            {i18n.t('Integrations:webhookReturn')}
          </label>
        </div>
        <Input
          type="url"
          value={returnUrl}
          placeholder="https://..."
          disabled={!returnEnabled}
          onChange={(e) => setReturnUrl(e.target.value)}
        />
      </div>

      <div className="mb-3">
        <label className="form-label fw-bold">
          {i18n.t('Integrations:webhookApiKey')}
        </label>
        <p className="text-muted small">
          {i18n.t('Integrations:webhookApiKeyHelp')}
        </p>
        <Input
          type="password"
          value={apiKey}
          autoComplete="new-password"
          placeholder={
            apiKeyIsSet
              ? i18n.t('Integrations:webhookApiKeySet')
              : i18n.t('Integrations:webhookApiKeyNotSet')
          }
          onChange={(e) => setApiKey(e.target.value)}
        />
      </div>

      {/* data-no-bs: shadcn Button inside this tab's Bootstrap scope. */}
      <div data-no-bs>
        <Button onClick={save} disabled={isSaving}>
          {isSaving ? <Spinner /> : i18n.t('Common:save')}
        </Button>
      </div>
    </EditItem>
  );
};
